import React, { useEffect, useState } from "react";
import { KeyRound, RefreshCw, ShieldCheck, Copy, AlertTriangle, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton } from "../components/ui";
import { api } from "../lib/api";
import PasskeySection from "../components/PasskeySection";
import EmailChange from "../components/EmailChange";
import DeleteAccount from "../components/DeleteAccount";
import { useTranslation } from "react-i18next";

export default function Security() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.myServiceCode().then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!window.confirm(t("support.rotateConfirm"))) return;
    setBusy(true);
    try { const { data: d } = await api.rotateServiceCode(); setData({ ...data, ...d, write_approved: false }); toast.success(t("support.rotated")); }
    catch { toast.error(t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const toggleWrite = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.setWriteApproval(!data.write_approved);
      setData({ ...data, write_approved: d.write_approved });
    } catch { toast.error(t("dialogs.failed")); }
    finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard?.writeText(data.code);
    toast.success(t("support.copied"));
  };

  if (!data) return <Skeleton className="h-48" />;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <span className="label">{t("support.subtitle")}</span>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("support.title")}</h1>
      </div>

      <PasskeySection />

      <Panel className="card p-5 sm:p-6" testid="service-code-panel">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound size={18} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("support.codeTitle")}</h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{t("support.codeBody")}</p>

        <div className="mt-4 flex items-center gap-2">
          <code data-testid="service-code"
                className="flex-1 tabular tracking-[0.2em] text-lg font-semibold bg-slate-50 rounded-xl px-4 py-3 text-center">
            {data.code}
          </code>
          <button className="btn-outline px-3" onClick={copy} data-testid="copy-code" title={t("support.copy")}>
            <Copy size={16} />
          </button>
          <button className="btn-outline px-3" onClick={rotate} disabled={busy} data-testid="rotate-code"
                  title={t("support.rotate")}>
            <RefreshCw size={16} />
          </button>
        </div>

        <div className="mt-3 flex gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-3">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <p>{t("support.codeWarning")}</p>
        </div>
      </Panel>

      <Panel className="card p-5 sm:p-6" testid="write-approval-panel">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={18} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("support.writeTitle")}</h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{t("support.writeBody")}</p>

        <label className="mt-4 flex items-center justify-between gap-4 cursor-pointer">
          <span className="text-sm font-medium text-slate-800">
            {data.write_approved ? t("support.writeOn") : t("support.writeOff")}
          </span>
          <button
            data-testid="toggle-write-approval"
            onClick={toggleWrite}
            disabled={busy}
            className={`relative w-12 h-6 rounded-full transition-colors ${data.write_approved ? "bg-brand-600" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${data.write_approved ? "translate-x-6" : "translate-x-0.5"}`} />
          </button>
        </label>
      </Panel>

      <EmailChange />

      <DeleteAccount />
    </div>
  );
}
